import { useEffect, useId, useState } from "react";
import { COUNTRY_ORIGIN_BANNER_ENABLED } from "@/utils/constants.js";
import { countryDisplayName } from "@/utils/countryDisplay.js";
import { countryFlagUrl } from "@/utils/countryFlag.js";
import "./country-origin-banner.css";

/**
 * Square country flag badge at the top-left of product images.
 * Country name is hidden by default; shown on hover (desktop) or tap (touch).
 */
export default function CountryOriginBanner({ country, size = "sm", className = "" }) {
    const tooltipId = useId();
    const [nameOpen, setNameOpen] = useState(false);
    const [hoverCapable, setHoverCapable] = useState(true);
    const [imageFailed, setImageFailed] = useState(false);

    const hasCountry = !!(country?.isoCode || country?.flagEmoji);
    const label = hasCountry ? countryDisplayName(country) : "";
    const flagSrc = country?.isoCode ? countryFlagUrl(country.isoCode) : null;
    const showFlagImage = !!(flagSrc && !imageFailed);

    useEffect(() => {
        const mediaQuery = window.matchMedia("(hover: hover) and (pointer: fine)");
        const update = () => setHoverCapable(mediaQuery.matches);
        update();
        mediaQuery.addEventListener("change", update);
        return () => mediaQuery.removeEventListener("change", update);
    }, []);

    useEffect(() => {
        setImageFailed(false);
    }, [country?.isoCode]);

    useEffect(() => {
        if (!nameOpen || hoverCapable) {
            return undefined;
        }
        const close = () => setNameOpen(false);
        document.addEventListener("click", close);
        return () => document.removeEventListener("click", close);
    }, [nameOpen, hoverCapable]);

    if (!COUNTRY_ORIGIN_BANNER_ENABLED || !hasCountry) {
        return null;
    }

    const handleTriggerClick = (event) => {
        if (hoverCapable) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        setNameOpen((open) => !open);
    };

    return (
        <div
            className={`country-origin-fold country-origin-fold--${size}${className ? ` ${className}` : ""}`}
        >
            <button
                type="button"
                className="country-origin-fold__trigger"
                aria-label={label ? `Origine : ${label}` : undefined}
                aria-describedby={label ? tooltipId : undefined}
                aria-expanded={!hoverCapable ? nameOpen : undefined}
                onClick={handleTriggerClick}
            >
                <span className="country-origin-fold__wrap" aria-hidden="true">
                    {showFlagImage ? (
                        <img
                            src={flagSrc}
                            alt=""
                            className="country-origin-fold__flag"
                            loading="lazy"
                            decoding="async"
                            onError={() => setImageFailed(true)}
                        />
                    ) : (
                        <span className="country-origin-fold__emoji">
                            {country.flagEmoji}
                        </span>
                    )}
                </span>
            </button>
            {label && (
                <span
                    id={tooltipId}
                    role="tooltip"
                    className={`country-origin-fold__label${!hoverCapable && nameOpen ? " is-visible" : ""}`}
                >
                    {label}
                </span>
            )}
        </div>
    );
}
